/**
 * The chart header: instrument identity plus the latest reading.
 *
 * Same metrics, same order as the annotation printed on the existing Quantist
 * chart, so someone moving from the Telegram image to the web reads the same
 * line. Every value arrives as a number and is formatted here.
 */
import type { WebChart } from "../domain/chart.ts";
import {
	correlationTone,
	formatDate,
	formatDelta,
	formatMoney,
	formatPercent,
	formatPrice,
	NO_VALUE,
} from "../domain/format.ts";

interface MetricProps {
	label: string;
	value: string;
	tone?: string;
	note?: string;
}

function Metric({ label, value, tone, note }: MetricProps) {
	return (
		<div className="metric" data-tone={tone}>
			<span className="metric__label">{label}</span>
			<span className="metric__value">{value}</span>
			{note ? <span className="metric__note">{note}</span> : null}
		</div>
	);
}

interface SummaryHeaderProps {
	chart: WebChart;
	/** The Foreign/Whale control, injected so the header stays presentational. */
	flowToggle?: React.ReactNode;
}

export function SummaryHeader({ chart, flowToggle }: SummaryHeaderProps) {
	const { summary, instrument, annotations, period } = chart;
	const abbrev = annotations.abbrev;
	const closeBelowVwap = summary.close !== null && summary.vwap !== null && summary.close < summary.vwap;

	return (
		<header className="summary">
			<div className="summary__identity">
				<div className="summary__title-row">
					<h1 className="summary__title">{instrument.display_name}</h1>
					<span className="tag tag--kind">{instrument.kind}</span>
					{/* The method still has to be readable with no toggle injected. */}
					{flowToggle ? null : <span className="tag tag--method">{annotations.method_label}</span>}
				</div>
				<p className="summary__date">
					{formatDate(summary.date)} · {period.bars} bars from {formatDate(period.start)}
				</p>
			</div>

			<div className="summary__metrics" role="group" aria-label="Latest reading">
				<Metric
					label="Close"
					value={formatPrice(summary.close)}
					tone={closeBelowVwap ? "down" : "up"}
					note={`${formatDelta(summary.close, summary.vwap)} vs VWAP`}
				/>
				<Metric label={`${abbrev}-VWAP (${period.params.vwap ?? NO_VALUE})`} value={formatPrice(summary.vwap, 2)} />
				<Metric
					label={`${abbrev} Money Flow (${period.params.mf ?? NO_VALUE})`}
					value={formatMoney(summary.money_flow)}
					tone={summary.money_flow !== null && summary.money_flow < 0 ? "down" : "up"}
				/>
				<Metric label={`${abbrev}-Prop (${period.params.prop ?? NO_VALUE})`} value={formatPercent(summary.proportion)} />
				<Metric label={`${abbrev}-NetProp (${period.params.prop ?? NO_VALUE})`} value={formatPercent(summary.net_proportion)} />
				<Metric
					label={`${abbrev}-Corr (${period.params.pricecorrel ?? NO_VALUE})`}
					value={formatPercent(summary.price_correlation)}
					tone={correlationTone(summary.price_correlation)}
				/>
				<Metric
					label={`MA ${abbrev}-Corr (${period.params.mapricecorrel ?? NO_VALUE})`}
					value={formatPercent(summary.ma_price_correlation)}
					tone={correlationTone(summary.ma_price_correlation)}
				/>
				<Metric label={`${abbrev}-Power`} value={summary.power_label ?? NO_VALUE} tone={summary.power_label ?? "none"} />
			</div>

			{/* The action area. Switching method is a control, not part of the
			    instrument's name, so it sits with the header's other actions
			    rather than inline with the title. */}
			{flowToggle ? <div className="summary__actions">{flowToggle}</div> : null}
		</header>
	);
}
